<template>
  <error-boundary module="view-manager-module">
    <hr-module class="employee-details">
      <hr-module-header :title="t('view-manager-module:title')">
        <div class="employee-details__title">
          <h3>
            {{ state.employeeName || t('view-manager-module:loading') }}
          </h3>
          <p class="employee-details__updated-at">
            {{ state.updatedAt || t('view-manager-module:loading') }}
          </p>
        </div>
        <co-icon
          class="employee-details__print"
          @click="print"
          name="print-solid"
        ></co-icon>
      </hr-module-header>

      <h-r-module-section class="employee-details__container">
        <div
          v-for="(document, index) in state.documents"
          :key="index"
          class="employee-details__card DSCard-container"
        >
          <slot namee="header">
            <div class="employee-details__header DSCard-header">
              <div class="employee-details__card__title">
                <span>{{ document.title }}</span>

                <!-- TODO: HACK to show the TFN form, make it generic -->
                <co-button
                  v-if="
                    document.name === 'tfnDeclaration' && state.showExportToAto
                  "
                  type="text"
                  class="employee-details__card__action-button"
                  @click="goToTfnDeclaration"
                >
                  {{ t('view-manager-module:export-ato') }}
                </co-button>
              </div>
            </div>
          </slot>

          <hr-field-view
            v-for="(field, index) in document.fields"
            :key="`field-${index}`"
            :value="field"
          />
        </div>
      </h-r-module-section>
    </hr-module>
  </error-boundary>
</template>

<script>
import HrModule from '@/components/hr-module';
import HrFieldView from '@/components/hr-field-view.vue';
import HRModuleSection from '@/components/HRModuleSection';
import HrModuleHeader from '@/components/hr-module-header.vue';
import store from '@/store';
import router from '@/router';
import setGlobalApiEndpoint from '@/lib/set-global-api-endpoint';
import jwtToObject from '@/lib/jwt-to-object';
import { DateTime } from 'luxon';
import { reactive } from 'vue';
import { CoButton, CoIcon } from '@deputy/copilot2';
import t from '@/lib/i18next';
import { logWebHrError } from '@/core/logger/log-wrapper';
import ErrorBoundary from '@/components/error-boundary.vue';

export default {
  name: 'view-manager-module',
  components: {
    ErrorBoundary,
    CoButton,
    CoIcon,
    HrModule,
    HRModuleSection,
    HrModuleHeader,
    HrFieldView,
  },
  props: {
    jwt: {
      type: String,
      default: null,
    },
    onboardingId: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    if (!props.jwt) {
      return router.push({ name: 'BlockModule' });
    }

    const stateModule = store.state;

    const state = reactive({
      employeeName: '',
      updatedAt: '',
      documents: [],
      showExportToAto: false,
    });

    function goToTfnDeclaration() {
      router.push({
        name: 'before-print-editing',
        // params: {document},
      });
    }

    function print() {
      window.print();
    }

    // TODO: find a better to filter out values like this
    function documentsToPreview(documents = []) {
      return documents.map((document) => {
        switch (document.name) {
          case 'superannuation':
            return documentsToPreviewSuperAnnuation(document);
          case 'tfnDeclaration':
            return documentsToPreviewTfnDeclaration(document);
          case 'taxDetails':
            return documentsToPreviewTaxDetails(document);
        }
        return document;
      });
    }

    // TODO: this has to be more generic
    function documentsToPreviewSuperAnnuation(document = {}) {
      const [superannuationOption] = document.fields;

      if (superannuationOption.value === 'No') {
        for (let i = 1; i < document.fields.length; i++) {
          document.fields[i].hide = true;
        }
      }

      return document;
    }

    // TODO: this has to be more generic
    function documentsToPreviewTfnDeclaration(document = {}) {
      if (document.fields instanceof Array) {
        for (let i = 0; i < document.fields.length; i++) {
          if (
            document.fields[i] &&
            document.fields[i].name === 'tfnDeclaration.option'
          ) {
            if (document.fields[i].value === 'No') {
              return document;
            }
          }
        }

        state.showExportToAto = true;
      }

      return document;
    }
    function documentsToPreviewTaxDetails(document = {}) {
      const p45Upload = document.fields.find(
        (item) => item.name === 'taxDetails.p45upload'
      );

      if (!p45Upload) {
        return document;
      }

      p45Upload.hide = true;

      return document;
    }

    function dateToHuman(updatedAt = new Date().toISOString()) {
      const updatedDate = DateTime.fromISO(updatedAt);
      const updated = updatedDate.toObject();
      updated.day = String(updated.day).padStart('2', '0');
      updated.month = String(updated.month).padStart('2', '0');

      return t(
        `view-manager-module:${
          ['APPROVED', 'SUBMITTED'].includes(stateModule.onboarding.state)
            ? 'submitted'
            : 'updated'
        }`,
        {
          ...updated,
          interpolation: { escapeValue: false },
        }
      );
    }

    async function initialize() {
      try {
        // TODO: implement temp jwt exchanging
        // const jwt = await $svcUrl.auth.token(props.jwt);

        setGlobalApiEndpoint(jwtToObject(props.jwt));

        await Promise.all([
          store.commit('setSession', {
            token: props.onboardingId,
            jwt: props.jwt,
          }),
          store.dispatch('getDocuments', props.onboardingId),
        ]);

        // TODO: remove the computed and add here the logic
        state.documents = documentsToPreview(stateModule.documents);
        state.employeeName = stateModule.Employee.employee.name;

        const { updatedAt } = stateModule.onboarding;
        state.updatedAt = dateToHuman(updatedAt);
      } catch (e) {
        logWebHrError({
          message: 'view manager module error',
          source: 'view-manager-module',
          metadata: {
            onboardingId: props.onboardingId,
            documentLength: stateModule.documents?.length,
            hasEmployeeName: !!stateModule.Employee?.employee?.name,
            updatedAt: stateModule.onboarding?.updatedAt,
          },
          error: e,
        });
        return router.push({ name: 'BlockModule' });
      }
    }

    initialize();

    return {
      t,
      state,
      stateModule,
      print,
      goToTfnDeclaration,
    };
  },
};
</script>

<style scoped lang="scss">
@import '~@/core/style-vars.scss';

.employee-details {
  padding-bottom: 50px;
}
.employee-details .hr-module-header {
  margin-top: 60px;
  margin-bottom: 40px;
}
.employee-details__DSCards {
  margin-bottom: 100px;
}
.employee-details__container {
  margin-top: 25px;
}
.employee-details__card {
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid var(--co-lines-border-transparent);
}
.employee-details__card__header {
  display: flex;
  border-bottom: 8px;
  border-color: var(--co-lines-border-solid);
}
.employee-details__card__title .DSIcon {
  color: var(--co-text-secondary);
}
.employee-details__card__title strong {
  color: var(--co-text-primary);
  margin-left: 5px;
}
.employee-details__card__title {
  flex-grow: 1;
  text-align: left;
  font-size: 18px;
  position: relative;
  padding: 0 6px;
  margin-bottom: 12px;
}
.employee-details__card__title span {
  font-weight: 600;
  color: var(--co-text-primary);
}
.employee-details__print {
  cursor: pointer;
  color: $co-button-default-text;
  position: absolute;
  right: 20px;
  top: 25px;
  width: 20px !important;
  height: 20px !important;
}
.employee-details__updated-at {
  color: var(--co-text-primary);
}
.employee-details__title {
  color: var(--co-text-primary);
  margin-bottom: 100px;
}
.employee-details__card__action-button {
  float: right;
  font-size: 14px !important;
}
@media (max-width: 767px) {
  .employee-details .hr-module-header {
    margin-top: 0;
    font-size: 14px;
    text-align: left;
  }
  .employee-details__title h1 {
    margin-bottom: 0px;
    font-size: 18px;
  }
  .employee-details__print {
    right: 18px;
    font-size: 18px;
  }
}
</style>
